import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { FORGOT_PASSWORD_ROUTE } from "@/constants/routes";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import SupportModal from "@/components/SupportModal";
import { LifeBuoy } from "lucide-react";

function Auth() {
  const navigate = useNavigate();
  const location = useLocation();
  // Check if "Join Now" was clicked (passed via state) or query param
  const isJoinMode = location.state?.showCuratorPrompt;
  const urlParams = new URLSearchParams(location.search);
  const queryMode = urlParams.get('mode');
  
  const [mode, setMode] = useState(queryMode === "signup" || isJoinMode ? "signup" : "signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // Check if user is already logged in
  useEffect(() => {
    let mounted = true;
    console.log("Auth page mounted, checking session...");

    const initAuth = async () => {
      try {
        // Check URL for logout param (for testing)
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('logout') === 'true') {
          console.log("Forcing logout for testing...");
          await supabase.auth.signOut();
          window.history.replaceState({}, '', '/auth');
          if (mounted) setCheckingAuth(false);
          return;
        }
        
        // Check for existing session
        const { data: { session } } = await supabase.auth.getSession();
        console.log("Auth page - session check result:", session ? "has session" : "no session");
        
        if (session && mounted) {
          navigate("/", { replace: true });
          return;
        }
      } catch (error) {
        console.error("Auth session check error:", error);
      } finally {
        if (mounted) {
          console.log("Auth page - setting checkingAuth to false");
          setCheckingAuth(false);
        }
      }
    };
    
    initAuth();

    return () => {
      mounted = false;
    };
  }, [navigate]);

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#808080]">Loading...</div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("[Auth] handleSubmit called, mode:", mode, "email:", email);
    setErrorMessage("");
    setLoading(true);
    try {
      if (mode === "signup") {
        if (password !== confirmPassword) {
          toast.error("Passwords do not match");
          setLoading(false);
          return;
        }
        
        const redirectUrl = `${window.location.origin}/`;
        
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: redirectUrl,
            data: { 
              username: email.split("@")[0] 
            }
          },
        });
        
        if (error) throw error;
        
        // Check if user was created and confirmed (auto-confirm is enabled)
        if (data.user && data.session) {
          toast.success("Account created! Welcome!");
          navigate("/");
        } else if (data.user) {
          toast.success("Account created. Check your email to confirm.");
          setMode("signin");
        }
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ 
          email, 
          password 
        });
        
        if (error) throw error;
        
        toast.success("Signed in successfully!");
        // Dispatch custom event to notify Layout and other components of login
        window.dispatchEvent(new CustomEvent('auth:login'));
        navigate("/");
      }
    } catch (err) {
      console.error("Auth error:", err);
      let message = err?.message || "Authentication failed";
      
      // Provide user-friendly error messages
      if (message.includes("Invalid login credentials")) {
        message = "Invalid email or password. Please try again.";
      } else if (message.includes("User already registered")) {
        message = "This email is already registered. Please sign in instead.";
      } else if (message.includes("Password should be")) {
        message = "Password must be at least 6 characters long.";
      }
      
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <Card className="w-full max-w-md bg-[#121212] text-[#e0e0e0] border border-[#1f1f1f]">
        <CardHeader>
          <CardTitle className="text-center text-2xl">
            {mode === "signin" ? "Sign In" : "Create Account"}
          </CardTitle>
          <div className="flex justify-center gap-2 text-sm text-[#a0a0a0]">
            <button
              className={`px-3 py-1 rounded ${mode === "signin" ? "bg-[#1f1f1f]" : ""}`}
              onClick={() => setMode("signin")}
              type="button"
            >
              Sign In
            </button>
            <button
              className={`px-3 py-1 rounded ${mode === "signup" ? "bg-[#1f1f1f]" : ""}`}
              onClick={() => setMode("signup")}
              type="button"
            >
              Sign Up
            </button>
          </div>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit}>
            {errorMessage && (
              <div className="text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-lg p-3">
                {errorMessage}
              </div>
            )}
            <div>
              <label className="text-sm text-[#a0a0a0]">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0]"
              />
            </div>
            <div>
              <label className="text-sm text-[#a0a0a0]">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyUp={(e) => setCapsLockOn(e.getModifierState && e.getModifierState("CapsLock"))}
                onKeyDown={(e) => setCapsLockOn(e.getModifierState && e.getModifierState("CapsLock"))}
                required
                className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0]"
              />
              {capsLockOn && (
                <p className="mt-1 text-xs text-yellow-400">Caps Lock is on</p>
              )}
            </div>
            {mode === "signup" && (
              <div>
                <label className="text-sm text-[#a0a0a0]">Confirm Password</label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="bg-[#0f0f0f] border-[#1f1f1f] text-[#e0e0e0]"
                />
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#808080]">
                {mode === "signin" ? "Need an account?" : "Already have an account?"}
              </span>
              <button
                type="button"
                className="text-[#b0b0b0] hover:underline"
                onClick={() => navigate(FORGOT_PASSWORD_ROUTE)}
              >
                Forgot password?
              </button>
            </div>
            <Button type="submit" disabled={loading} className="w-full">
              {loading ? "Working..." : mode === "signin" ? "Sign In" : "Sign Up"}
            </Button>
          </form>
          
          <div className="mt-6 pt-4 border-t border-[#1f1f1f] text-center">
            <SupportModal 
              trigger={
                <button className="text-[#808080] hover:text-[#a0a0a0] text-xs flex items-center gap-1.5 mx-auto transition-colors">
                  <LifeBuoy className="w-3.5 h-3.5" />
                  Need help? Contact support
                </button>
              }
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default Auth;
